import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";

import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      position: "bottom",
      labels: { font: { size: 12, family: "Inter, Segoe UI, sans-serif" }, padding: 16 },
    },
    tooltip: {
      backgroundColor: "#1A202C",
      titleColor: "#A0AEC0",
      bodyColor: "#ffffff",
      cornerRadius: 8,
      padding: 10,
      callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}%` },
    },
  },
  scales: {
    x: { ticks: { font: { size: 11 }, color: "#718096", maxRotation: 45 }, grid: { display: false } },
    y: { min: 0, max: 100, ticks: { font: { size: 11 }, color: "#718096", callback: (v) => `${v}%` }, grid: { color: "#EDF2F7" } },
  },
};

const DivisionDetails = () => {
  const { divisionId } = useParams();
  const location = useLocation();
  const divisionName = location.state?.divisionName;
  const Api = process.env.REACT_APP_API;
  const navigate = useNavigate();

  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [activeJfmc, setActiveJfmc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchRanges = async () => {
      try {
        const config = Authorization();
        const res = await axios.get(
          `${Api}/api/review-dashboard/review-dashboard/division/${divisionId}`,
          config
        );
        const payload = res.data;
        const jfmcArray = Array.isArray(payload) ? payload : [payload];
        setJfmcTypes(jfmcArray);
        setActiveJfmc(jfmcArray[0]);
      } catch (err) {
        console.error(err);
        setError("Unable to load ranges");
      } finally {
        setLoading(false);
      }
    };
    fetchRanges();
  }, [Api, divisionId]);

  if (loading) return <Preloader loading={true} />;
  if (error) return <p className="text-danger">{error}</p>;
  if (!activeJfmc) return null;

  const { ranges = [], totals = {} } = activeJfmc;

  const badge = (v, type) => {
    const zero = v === 0;
    const s = type === "total"
      ? { bg: "#E8EDF5", cl: "#3A5080", br: "#C5D0E6" }
      : type === "done"
        ? zero ? { bg: "#F1F4F9", cl: "#9BA8BF", br: "#DDE3EE" } : { bg: "#D1EFD8", cl: "#14532D", br: "#A3D9B0" }
        : zero ? { bg: "#D1EFD8", cl: "#14532D", br: "#A3D9B0" } : { bg: "#FFE0D0", cl: "#7C2200", br: "#FFBFA0" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 36, padding: "3px 10px", borderRadius: "50rem", fontSize: 12, fontWeight: 700, background: s.bg, color: s.cl, border: `1px solid ${s.br}` }}>
        {v}
      </span>
    );
  };

  const CombinedChart = ({ ranges, title }) => {
    const labels = ranges.map((r) => r.rangeName);
    return (
      <div className="card dash-chart-card mt-4">
        <div className="card-header dash-chart-header">
          <span className="dash-chart-icon dash-icon-purple">
            <i className="mdi mdi-chart-line" />
          </span>
          <div>
            <div className="dash-chart-title">{title}</div>
            <div className="dash-chart-sub">Completion percentage by range</div>
          </div>
        </div>
        <div className="card-body dash-chart-body">
          <div style={{ height: 320, width: "100%" }}>
            <Line
              data={{
                labels,
                datasets: [
                  { label: "EC %", data: ranges.map((r) => r.totalVfc ? ((r.ecDone / r.totalVfc) * 100).toFixed(1) : 0), borderColor: "#2E7D32", backgroundColor: "rgba(46,125,50,0.08)", tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6 },
                  { label: "GB %", data: ranges.map((r) => r.totalVfc ? ((r.gbDone / r.totalVfc) * 100).toFixed(1) : 0), borderColor: "#1976D2", backgroundColor: "rgba(25,118,210,0.06)", tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6 },
                  { label: "Audit %", data: ranges.map((r) => r.totalVfc ? ((r.auditDone / r.totalVfc) * 100).toFixed(1) : 0), borderColor: "#7B1FA2", backgroundColor: "rgba(123,31,162,0.06)", tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6 },
                  { label: "Renewal %", data: ranges.map((r) => r.totalVfc ? ((r.renewalDone / r.totalVfc) * 100).toFixed(1) : 0), borderColor: "#E65100", backgroundColor: "rgba(230,81,0,0.06)", tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6 },
                ],
              }}
              options={lineChartOptions}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="rd-page-header">
        <div className="rd-header-left">
          <button
            className="btn btn-sm btn-outline-secondary me-3"
            style={{ borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: 12 }}
            onClick={() => navigate(-1)}
          >
            <i className="mdi mdi-arrow-left me-1" /> Back
          </button>
          <span className="page-title-icon">
            <i className="mdi mdi-pine-tree" />
          </span>
          <div>
            <h3 className="rd-page-title">{divisionName || "Division"}</h3>
            <p className="rd-page-sub">Range-wise compliance summary</p>
          </div>
        </div>
        <div className="jfmc-switch">
          {jfmcTypes.map((j) => (
            <button
              key={j.jfmcTypeId}
              className={`btn ${activeJfmc?.jfmcTypeId === j.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setActiveJfmc(j)}
            >
              {j.jfmcTypeName}
            </button>
          ))}
        </div>
      </div>

      {/* ── JFMC Section ── */}
      <div className="jfmc-section">
        <h3 className="jfmc-title">{activeJfmc.jfmcTypeName} — Compliance Overview</h3>

        {/* ── KPI Cards ── */}
        <div key={activeJfmc.jfmcTypeId} style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 }}>
          <div className="stat-card card-green stat-centered">
            <div className="stat-icon-big icon-green"><i className="fas fa-users-cog" /></div>
            <div className="stat-title">EC Meetings</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.ecDone}</div><div className="mini-label">Held</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.ecNotDone}</div><div className="mini-label">Not Held</div></div>
            </div>
          </div>
          <div className="stat-card card-blue stat-centered">
            <div className="stat-icon-big icon-blue"><i className="fas fa-users" /></div>
            <div className="stat-title">General Body</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.gbDone}</div><div className="mini-label">Held</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.gbNotDone}</div><div className="mini-label">Not Held</div></div>
            </div>
          </div>
          <div className="stat-card card-orange stat-centered">
            <div className="stat-icon-big icon-orange"><i className="fas fa-file-signature" /></div>
            <div className="stat-title">Audit</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.auditDone}</div><div className="mini-label">Audited</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.auditNotDone}</div><div className="mini-label">Pending</div></div>
            </div>
          </div>
          <div className="stat-card card-purple stat-centered">
            <div className="stat-icon-big icon-purple"><i className="fas fa-sync-alt" /></div>
            <div className="stat-title">Renewal</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.renewalDone}</div><div className="mini-label">Renewed</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.renewalNotDone}</div><div className="mini-label">Pending</div></div>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ marginTop: 24, background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7" }}>
            <span style={{ width: 36, height: 36, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, flexShrink: 0 }}>
              <i className="mdi mdi-table-large" />
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1A202C" }}>{activeJfmc.jfmcTypeName} — Range-wise Summary</div>
              <div style={{ fontSize: 11, color: "#A0AEC0", fontWeight: 500, marginTop: 2 }}>Click a range name to drill down</div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#2E7D32" }}>
                  <th colSpan="3" style={{ background: "#1B5E20", color: "rgba(255,255,255,0.7)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.6px", padding: "11px 16px", fontWeight: 700, textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.12)" }} />
                  {[
                    { label: "EC Meetings", icon: "fas fa-users-cog" },
                    { label: "General Body", icon: "fas fa-users" },
                    { label: "Audit", icon: "fas fa-file-signature" },
                    { label: "Renewal", icon: "fas fa-sync-alt" },
                  ].map(({ label, icon }, i) => (
                    <th key={label} colSpan="2" style={{ background: "#2E7D32", color: "#ffffff", textAlign: "center", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", padding: "11px 14px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
                      <i className={icon} style={{ marginRight: 6, opacity: 0.85 }} />{label}
                    </th>
                  ))}
                </tr>
                <tr style={{ background: "#E8F5E9" }}>
                  {["Range", "Last Submitted", "Total"].map((h, i) => (
                    <th key={h} style={{ background: "#E8F5E9", color: "#1B5E20", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.6px", padding: "9px 16px", fontWeight: 700, whiteSpace: "nowrap", textAlign: i === 2 ? "center" : "left", borderRight: i === 2 ? "1px solid #C8E6C9" : "none", borderBottom: "2px solid #A5D6A7" }}>
                      {h}
                    </th>
                  ))}
                  {["Held", "Not Held", "Held", "Not Held", "Audited", "Pending", "Renewed", "Pending"].map((h, i) => (
                    <th key={i} style={{ background: "#E8F5E9", color: "#1B5E20", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", padding: "9px 12px", fontWeight: 700, textAlign: "center", whiteSpace: "nowrap", borderRight: i % 2 === 1 ? "1px solid #C8E6C9" : "none", borderBottom: "2px solid #A5D6A7" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ranges
                  .sort((a, b) => a.rangeName.localeCompare(b.rangeName))
                  .map((r, rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? "#ffffff" : "#F8FAFD";
                    const tdBase = { padding: "10px 14px", borderBottom: "1px solid #EDF2F7", background: rowBg, verticalAlign: "middle" };
                    return (
                      <tr key={r._id}
                        onMouseEnter={e => [...e.currentTarget.cells].forEach(td => td.style.background = "#EEF3FF")}
                        onMouseLeave={e => [...e.currentTarget.cells].forEach(td => td.style.background = rowBg)}
                      >
                        <td
                          style={{ ...tdBase, padding: "10px 16px", color: "#1565C0", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                          onClick={() => navigate(`/review-dashboard/range/${r._id}`, { state: { rangeName: r.rangeName } })}
                        >
                          <i className="mdi mdi-map-marker-outline" style={{ marginRight: 5, color: "#64B5F6", fontSize: 12 }} />{r.rangeName}
                        </td>
                        <td style={{ ...tdBase, padding: "10px 16px", color: "#718096", fontSize: 12, whiteSpace: "nowrap" }}>
                          {r.lastSubmittedAt ? new Date(r.lastSubmittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : <span style={{ color: "#CBD5E0", fontStyle: "italic", fontSize: 11 }}>N/A</span>}
                        </td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "3px solid #E2E8F0" }}>{badge(r.totalVfc, "total")}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{badge(r.ecDone, "done")}</td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "3px solid #E2E8F0" }}>{badge(r.ecNotDone, "pend")}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{badge(r.gbDone, "done")}</td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "3px solid #E2E8F0" }}>{badge(r.gbNotDone, "pend")}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{badge(r.auditDone, "done")}</td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "3px solid #E2E8F0" }}>{badge(r.auditNotDone, "pend")}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{badge(r.renewalDone, "done")}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{badge(r.renewalNotDone, "pend")}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Chart ── */}
        <CombinedChart
          ranges={ranges}
          title={`${activeJfmc.jfmcTypeName} — Status (%)`}
        />
      </div>
    </div>
  );
};

export default DivisionDetails;
